import React from 'react'
import { FiArrowLeft, FiShield } from 'react-icons/fi'
import { HiAcademicCap } from 'react-icons/hi2'
import logo from '../../../images/app_logo.png'
import logo1 from '../../../images/logo1.png'

// Shared design language for every public parent-feedback screen — matches
// the reference mockups and the existing brand colors already used in
// utils/pdfGenerator.js's weekly report PDFs (#14213d / #f5a623), so the
// parent-facing web flow and the PDF reports feel like the same product.
export const BRAND_NAVY = '#082d86'
export const BRAND_NAVY_DEEP = '#0f2e85'
export const BRAND_GOLD = '#f5a623'
export const BG_SOFT = '#f7f8fb'

export const TEACHER_ACCENTS = [
  { avatar: '#dbe8fd', pillBg: '#e8f0fe', pillText: '#3b6fd9' },
  { avatar: '#d7f3e0', pillBg: '#e6f7ec', pillText: '#1e9e56' },
  { avatar: '#e9dcf9', pillBg: '#f1e9fb', pillText: '#8347c9' },
  { avatar: '#fbe6c4', pillBg: '#fdf1de', pillText: '#d1861f' },
  { avatar: '#fbd9de', pillBg: '#fdeaec', pillText: '#d1497a' }
]

// TopBar and Hero used to be two separate solid-navy divs stacked on top of
// each other, so the decorative blob shapes (added to Hero only) got cut off
// at the seam between them instead of spanning the whole navy panel like the
// reference design. They're now one continuous panel — the logo/back/shield
// row and the icon/title/subtitle block share the same gradient background,
// overflow-hidden, and blobs.
export function Hero({ icon: Icon, title, subtitle, onBack }) {
  return (
    <div className="relative px-6 pt-3 pb-8 text-center overflow-hidden" style={{ background: `linear-gradient(160deg, ${BRAND_NAVY} 0%, ${BRAND_NAVY_DEEP} 100%)` }}>
      {/* decorative gradient blobs, cut off by the panel's own corners */}
      <span
        className="pointer-events-none absolute -top-10 -right-12 w-40 h-40 rounded-[42%] opacity-40"
        style={{ background: 'linear-gradient(135deg, #4a7bea, #1e3fae)' }}
      />
      <span
        className="pointer-events-none absolute -bottom-14 -left-14 w-48 h-48 rounded-full opacity-30"
        style={{ background: 'linear-gradient(135deg, #3660d6, #14213d)' }}
      />

      {/* faint decorative pattern */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] text-white text-3xl select-none overflow-hidden">
        <span className="absolute left-[8%] top-[15%]">📖</span>
        <span className="absolute right-[10%] top-[20%] text-xl">✎</span>
        <span className="absolute left-[15%] bottom-[15%] text-xl">🎓</span>
        <span className="absolute right-[15%] bottom-[20%]">📘</span>
      </div>

      <div className="relative flex items-center justify-between gap-2 mb-4">
        <div className="w-14 text-left">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 text-[11px] font-medium text-white/90 hover:text-white">
              <FiArrowLeft size={15} /> Back
            </button>
          )}
        </div>
        <img className="w-[76px] object-contain flex-shrink-0" src={logo1} alt="My Academy Partner" />
        <div className="w-14 flex justify-end">
          <FiShield className="text-[19px] text-white/90 flex-shrink-0" />
        </div>
      </div>

      <div className="relative">
        <div className="mx-auto w-12 h-12 rounded-full bg-white flex items-center justify-center mb-4 shadow-lg">
          <Icon style={{ color: BRAND_NAVY }} size={36} />
        </div>
        <h1 className="text-white -mt-2 text-[16px] font-bold tracking-tight">{title}</h1>
        <div className="w-10 h-[3px] rounded-full mx-auto my-1.5" style={{ background: BRAND_GOLD }} />
        <p className="text-white/75 text-[12px] -pt-1 max-w-[300px] mx-auto leading-relaxed">{subtitle}</p>
      </div>
    </div>
  )
}

export function SectionLabel({ icon: Icon, title, subtitle, className = '' }) {
  return (
    <div className={`flex items-start gap-2.5 ${className}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: BRAND_NAVY }}>
        <Icon className="text-white" size={15} />
      </div>
      <div>
        <p className="font-medium text-gray-800 text-[14px] mt-1 leading-tight">{title}</p>
        {subtitle && <p className="text-[11.5px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )
}

export function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-[0_2px_4px_rgba(20,33,61,0.04),0_10px_28px_-6px_rgba(20,33,61,0.10)] p-5 ${className}`}>
      {children}
    </div>
  )
}

export function PrimaryButton({ children, loading, ...props }) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      style={{ background: BRAND_NAVY }}
      className="w-full text-white text-[12px] font-bold py-[13px] rounded-xl disabled:opacity-50 transition-all active:scale-[0.98] shadow-[0_6px_16px_-4px_rgba(20,33,61,0.4)] flex items-center justify-center gap-2 uppercase tracking-wide"
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

export function Spinner({ className = '' }) {
  return (
    <svg className={`animate-spin h-4 w-4 ${className}`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}

export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="text-center py-14 px-4">
      {Icon && (
        <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: BG_SOFT }}>
          <Icon size={24} style={{ color: BRAND_NAVY, opacity: 0.4 }} />
        </div>
      )}
      <p className="font-semibold text-gray-700 text-[15px]">{title}</p>
      {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
    </div>
  )
}

export function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 animate-pulse">
      <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-gray-200 rounded w-2/3" />
        <div className="h-2.5 bg-gray-100 rounded w-1/3" />
      </div>
      <div className="w-24 h-9 rounded-lg bg-gray-200 flex-shrink-0" />
    </div>
  )
}

export function FooterBand({ icon: Icon = null, title, subtitle }) {
  return (
    <div className="px-6 py-8 text-center" style={{ background: BRAND_NAVY }}>
      {Icon && (
        <div className="mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(245,166,35,0.15)' }}>
          <Icon style={{ color: BRAND_GOLD }} size={18} />
        </div>
      )}
      <p className="text-white font-bold text-[14.5px]">{title}</p>
      {subtitle && <p className="text-white/60 text-[12px] mt-1.5 max-w-xs mx-auto leading-relaxed">{subtitle}</p>}
    </div>
  )
}
